import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getCaptcha, apiError } from "@/lib/api";

export default function AdminLogin() {
  const { loginWithKey } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [key, setKey] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCaptcha = useCallback(() => {
    setAnswer("");
    getCaptcha().then(setCaptcha).catch(() => setCaptcha(null));
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha) { loadCaptcha(); return; }
    setLoading(true);
    try {
      await loginWithKey(key, captcha.token, answer);
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a1b33] px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="font-serif text-4xl font-semibold text-white tracking-tight">Editorial Harvest</div>
          <p className="hv-overline hv-overline-light mt-3">Panel de administración</p>
        </div>
        <form onSubmit={submit} className="bg-white p-8" data-testid="admin-login-form">
          <label className="block text-sm font-medium text-[#0f2544] mb-2">Clave de acceso</label>
          <div className="flex items-center border-2 border-[#0f2544] px-3">
            <Lock size={16} className="text-[#8a8a83]" />
            <input
              type="password" required autoFocus value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Introduce la clave"
              className="flex-1 px-3 py-3 text-sm outline-none"
              data-testid="login-key"
            />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#0f2544]">Verificación</label>
              <button type="button" onClick={loadCaptcha} className="text-[#8a8a83] hover:text-[#1f44e0] transition-colors" aria-label="Nueva verificación" data-testid="captcha-refresh">
                <RefreshCw size={14} />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-4 py-3 bg-[#eceae4] text-[#0f2544] font-serif text-lg font-semibold select-none min-w-[140px] text-center" data-testid="captcha-question">
                {captcha ? captcha.question : "Cargando..."}
              </span>
              <input
                type="text" inputMode="numeric" required value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Respuesta"
                className="flex-1 border-2 border-[#0f2544] px-3 py-3 text-sm outline-none"
                data-testid="captcha-answer"
              />
            </div>
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full mt-6 bg-[#0f2544] px-6 py-3.5 text-xs font-semibold tracking-[0.14em] uppercase text-white hover:bg-[#1f44e0] transition-colors disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? "Accediendo..." : "Acceder"}
          </button>
        </form>
        <p className="text-center text-xs text-white/35 mt-6">Acceso restringido a Editorial Harvest</p>
      </div>
    </div>
  );
}
